import type { NotificationEventType, NotificationSettingsEventKey } from "./types";

// Routes
export const NOTIFICATION_ROUTES = {
  LIST: "/notifications",
  SETTINGS: "/notifications/settings",
} as const;

// Polling cadence for the unread badge (no realtime push in V1).
// React Query pauses intervals for backgrounded tabs by default.
export const NOTIFICATION_POLLING = {
  UNREAD_COUNT_INTERVAL_MS: 30_000,
  UNREAD_COUNT_STALE_MS: 20_000,
} as const;

// Pagination
export const NOTIFICATION_PAGE_SIZE = 20;

/**
 * Wireframe category tabs → event-type groups. The list API has no server-side
 * category filter (only `unreadOnly`), so these drive CLIENT-side filtering of
 * the loaded notifications. "Messages" is intentionally absent — no chat/message
 * event type exists, so that tab would always be empty for students.
 */
export type NotificationCategory = "all" | "applications" | "interviews";

export const NOTIFICATION_CATEGORY_EVENTS = {
  applications: [
    "APPLICATION_SUBMITTED",
    "APPLICATION_HR_REVIEWED",
    "APPLICATION_FORWARDED",
    "CANDIDATE_SELECTED",
    "CANDIDATE_REJECTED",
    "APPLICATION_WITHDRAWN",
  ],
  interviews: ["INTERVIEW_SCHEDULED"],
} as const satisfies Record<Exclude<NotificationCategory, "all">, readonly NotificationEventType[]>;

export const NOTIFICATION_CATEGORY_TABS: ReadonlyArray<{
  key: NotificationCategory;
  label: string;
}> = [
  { key: "all", label: "All" },
  { key: "applications", label: "Applications" },
  { key: "interviews", label: "Interviews" },
];

// Badge caps at "9+" past this value
export const UNREAD_BADGE_MAX = 9;

// User-facing messages (toasts)
export const NOTIFICATION_MESSAGES = {
  MARK_READ_ERROR: "Couldn't mark notification as read",
  MARK_ALL_READ_ERROR: "Couldn't mark all as read",
  DELETE_SUCCESS: "Notification removed",
  DELETE_ERROR: "Couldn't remove notification",
  SETTINGS_SAVED: "Preferences saved",
  SETTINGS_ERROR: "Couldn't save preferences",
  MANUAL_SEND_SUCCESS: "Notification sent",
  MANUAL_SEND_ERROR: "Couldn't send notification",
} as const;

/** "Marked N as read" — pluralized. */
export const markedReadMessage = (count: number): string =>
  `Marked ${count} ${count === 1 ? "notification" : "notifications"} as read`;

// Display label per event type. `title`/`body` arrive pre-rendered from the
// backend; these labels are for the settings screen, grouping, and icons.
export const EVENT_TYPE_LABELS: Record<NotificationEventType, string> = {
  BOOKMARK_RECEIVED: "Bookmark received",
  FORMAL_REQUEST_CREATED: "Formal request created",
  APPLICATION_SUBMITTED: "Application submitted",
  APPLICATION_HR_REVIEWED: "Application reviewed by HR",
  APPLICATION_FORWARDED: "Application forwarded",
  INTERVIEW_SCHEDULED: "Interview scheduled",
  CANDIDATE_SELECTED: "Candidate selected",
  CANDIDATE_REJECTED: "Candidate rejected",
  APPLICATION_WITHDRAWN: "Application withdrawn",
  ENTERPRISE_REGISTERED: "Enterprise registered",
  ENTERPRISE_APPROVED: "Enterprise approved",
  ENTERPRISE_CLOSED: "Enterprise closed",
  JOB_POSTING_SUBMITTED: "Job posting submitted",
  JOB_POSTING_APPROVED: "Job posting approved",
  PROFILE_SUSPENDED: "Profile suspended",
  ZENCUBE_API_UNAVAILABLE: "ZenCube API unavailable",
};

/**
 * Recipient-perspective labels for the settings screen (from the backend's
 * suggested copy). The settings page only shows a user their *relevant* events,
 * so these first-person/role-scoped phrasings always match the viewer.
 */
export const SETTINGS_EVENT_LABELS: Record<NotificationEventType, string> = {
  BOOKMARK_RECEIVED: "An enterprise saved my profile",
  FORMAL_REQUEST_CREATED: "Enterprise raised a hiring request",
  APPLICATION_SUBMITTED: "New application submitted",
  APPLICATION_HR_REVIEWED: "My application has been reviewed",
  APPLICATION_FORWARDED: "My application has been forwarded",
  INTERVIEW_SCHEDULED: "Interview scheduled for me",
  CANDIDATE_SELECTED: "I was selected for a role",
  CANDIDATE_REJECTED: "My application was closed",
  APPLICATION_WITHDRAWN: "A student withdrew their application",
  ENTERPRISE_REGISTERED: "A new enterprise has signed up",
  ENTERPRISE_APPROVED: "My enterprise account was approved",
  ENTERPRISE_CLOSED: "An enterprise account was closed",
  JOB_POSTING_SUBMITTED: "A new job posting awaits review",
  JOB_POSTING_APPROVED: "My job posting was approved",
  PROFILE_SUSPENDED: "My profile was suspended",
  ZENCUBE_API_UNAVAILABLE: "ZenCube sync degraded",
};

/**
 * Per-event rows for the settings screen. `settingsKey` ties each toggle to the
 * matching boolean field on NotificationSettings{Response,Request}.
 */
export const EVENT_SETTINGS_FIELDS: ReadonlyArray<{
  eventType: NotificationEventType;
  settingsKey: NotificationSettingsEventKey;
  label: string;
}> = [
  {
    eventType: "BOOKMARK_RECEIVED",
    settingsKey: "bookmarkReceived",
    label: EVENT_TYPE_LABELS.BOOKMARK_RECEIVED,
  },
  {
    eventType: "FORMAL_REQUEST_CREATED",
    settingsKey: "formalRequestCreated",
    label: EVENT_TYPE_LABELS.FORMAL_REQUEST_CREATED,
  },
  {
    eventType: "APPLICATION_SUBMITTED",
    settingsKey: "applicationSubmitted",
    label: EVENT_TYPE_LABELS.APPLICATION_SUBMITTED,
  },
  {
    eventType: "APPLICATION_HR_REVIEWED",
    settingsKey: "applicationHrReviewed",
    label: EVENT_TYPE_LABELS.APPLICATION_HR_REVIEWED,
  },
  {
    eventType: "APPLICATION_FORWARDED",
    settingsKey: "applicationForwarded",
    label: EVENT_TYPE_LABELS.APPLICATION_FORWARDED,
  },
  {
    eventType: "INTERVIEW_SCHEDULED",
    settingsKey: "interviewScheduled",
    label: EVENT_TYPE_LABELS.INTERVIEW_SCHEDULED,
  },
  {
    eventType: "CANDIDATE_SELECTED",
    settingsKey: "candidateSelected",
    label: EVENT_TYPE_LABELS.CANDIDATE_SELECTED,
  },
  {
    eventType: "CANDIDATE_REJECTED",
    settingsKey: "candidateRejected",
    label: EVENT_TYPE_LABELS.CANDIDATE_REJECTED,
  },
  {
    eventType: "APPLICATION_WITHDRAWN",
    settingsKey: "applicationWithdrawn",
    label: EVENT_TYPE_LABELS.APPLICATION_WITHDRAWN,
  },
  {
    eventType: "ENTERPRISE_REGISTERED",
    settingsKey: "enterpriseRegistered",
    label: EVENT_TYPE_LABELS.ENTERPRISE_REGISTERED,
  },
  {
    eventType: "ENTERPRISE_APPROVED",
    settingsKey: "enterpriseApproved",
    label: EVENT_TYPE_LABELS.ENTERPRISE_APPROVED,
  },
  {
    eventType: "ENTERPRISE_CLOSED",
    settingsKey: "enterpriseClosed",
    label: EVENT_TYPE_LABELS.ENTERPRISE_CLOSED,
  },
  {
    eventType: "JOB_POSTING_SUBMITTED",
    settingsKey: "jobPostingSubmitted",
    label: EVENT_TYPE_LABELS.JOB_POSTING_SUBMITTED,
  },
  {
    eventType: "JOB_POSTING_APPROVED",
    settingsKey: "jobPostingApproved",
    label: EVENT_TYPE_LABELS.JOB_POSTING_APPROVED,
  },
  {
    eventType: "PROFILE_SUSPENDED",
    settingsKey: "profileSuspended",
    label: EVENT_TYPE_LABELS.PROFILE_SUSPENDED,
  },
  {
    eventType: "ZENCUBE_API_UNAVAILABLE",
    settingsKey: "zencubeApiUnavailable",
    label: EVENT_TYPE_LABELS.ZENCUBE_API_UNAVAILABLE,
  },
];

// Permission strings (gate the HR manual-send tool with useHasPermission)
export const NOTIFICATION_PERMISSIONS = {
  VIEW_OWN: "NOTIFICATION_VIEW_OWN",
  MANAGE_OWN: "NOTIFICATION_MANAGE_OWN",
  SEND_MANUAL: "NOTIFICATION_SEND_MANUAL",
} as const;
